import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useAllOrdersQuery } from '../orders/api';
import type { MerchantOrder } from '../orders/types';
import { useCouponsQuery } from '../promotions/api';
import type { Coupon, CouponUsage } from '../promotions/types';

import type { CustomerCouponUsage, CustomerSummary } from './types';

import { apiRequest } from '@/lib/api-client';
import { useSession } from '@/lib/session';

export interface CustomersResult {
  readonly customers: readonly CustomerSummary[];
  readonly truncated: boolean;
  readonly isPending: boolean;
  readonly isError: boolean;
}

/**
 * "Clients" — real orders (`useAllOrdersQuery`, no new endpoint) grouped
 * by `customerPhone`. `name` uses the most RECENT order's
 * `customerName` for that phone (a returning customer's name on file
 * can change between orders; the latest is the least stale real value,
 * never a fabricated "current" one).
 */
export function useCustomersQuery(): CustomersResult {
  const allOrders = useAllOrdersQuery();

  const customers = useMemo<readonly CustomerSummary[]>(() => {
    const byPhone = new Map<string, MerchantOrder[]>();
    for (const order of allOrders.orders) {
      const list = byPhone.get(order.customerPhone) ?? [];
      list.push(order);
      byPhone.set(order.customerPhone, list);
    }

    const result: CustomerSummary[] = [];
    for (const [phone, orders] of byPhone) {
      const sorted = [...orders].sort((a, b) => b.createdAt.localeCompare(a.createdAt));
      const mostRecent = sorted[0];
      if (mostRecent === undefined) continue;

      const totalSpent = orders
        .filter((order) => order.status !== 'CANCELLED')
        .reduce((sum, order) => sum + order.grandTotal, 0);

      result.push({
        phone,
        name: mostRecent.customerName,
        orderCount: orders.length,
        totalSpent,
        lastActivityAt: mostRecent.createdAt,
        orders: sorted,
      });
    }

    return result.sort((a, b) => b.lastActivityAt.localeCompare(a.lastActivityAt));
  }, [allOrders.orders]);

  return {
    customers,
    truncated: allOrders.truncated,
    isPending: allOrders.isPending,
    isError: allOrders.isError,
  };
}

export interface CustomerCouponUsagesResult {
  readonly usages: readonly CustomerCouponUsage[];
  readonly isPending: boolean;
}

/**
 * "Coupons utilisés" in the customer detail view — no endpoint returns
 * usages by customer, only by coupon (`GET /merchant/coupons/{id}/usages`,
 * the same route `CouponUsageDrawer` already calls). Fetches every real
 * coupon's usage list in parallel and keeps only the rows matching this
 * customer's phone — real data, filtered client-side, never a second
 * invented endpoint. Only fires while a customer detail is actually open
 * (`enabled` gated on `phone`), not on every visit to the Clients list.
 */
export function useCustomerCouponUsagesQuery(
  phone: string | undefined,
): CustomerCouponUsagesResult {
  const { accessToken, activeShopId } = useSession();
  const couponsQuery = useCouponsQuery();
  const coupons: readonly Coupon[] = couponsQuery.data ?? [];

  const usageQueries = useQueries({
    queries: coupons.map((coupon) => ({
      queryKey: ['coupons', activeShopId, coupon.id, 'usages'],
      queryFn: async () => {
        const response = await apiRequest<{ usages: readonly CouponUsage[] }>(
          `/merchant/coupons/${coupon.id}/usages`,
          { accessToken, shopId: activeShopId },
        );
        return { code: coupon.code, usages: response.usages };
      },
      enabled: accessToken !== null && activeShopId !== null && phone !== undefined,
    })),
  });

  const usages: CustomerCouponUsage[] = [];
  for (const query of usageQueries) {
    if (query.data === undefined) continue;
    for (const usage of query.data.usages) {
      if (usage.customerPhone === phone) {
        usages.push({
          couponCode: query.data.code,
          discountAmount: usage.discountAmount,
          orderNumber: usage.orderNumber,
          createdAt: usage.createdAt,
        });
      }
    }
  }
  usages.sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  return {
    usages,
    isPending: couponsQuery.isPending || usageQueries.some((query) => query.isPending),
  };
}
